import { useEffect } from "react";
import { readBg } from "@/components/BgToggle";

// Watches every <img> inside a `.checker` tile. Light (white) artwork flips the tile to a dark
// checkerboard; dark (black) artwork keeps it white — so text is always readable.
const cache = new Map();
const KEY = "awg_art_tone";
try { Object.entries(JSON.parse(sessionStorage.getItem(KEY) || "{}")).forEach(([k, v]) => cache.set(k, v)); } catch {}
const persist = () => { try { sessionStorage.setItem(KEY, JSON.stringify(Object.fromEntries([...cache].slice(-600)))); } catch {} };

const tone = (img) => {
  if (cache.has(img.src)) return cache.get(img.src);
  try {
    const S = 40, c = document.createElement("canvas"); c.width = S; c.height = S;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, S, S);
    const d = ctx.getImageData(0, 0, S, S).data;
    let n = 0, light = 0, transparent = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 40) { transparent++; continue; }
      n++;
      const l = 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
      if (l > 200) light++;
    }
    const total = n + transparent;
    // Only transparent art needs help; a fully opaque image already carries its own background.
    const t = total && transparent / total > 0.12 && n && light / n > 0.6 ? "dark" : "light";
    cache.set(img.src, t); persist();
    return t;
  } catch { return null; }
};

const apply = (img) => {
  const tile = img.closest(".checker");
  if (!tile || !img.complete || !img.naturalWidth) return;
  const t = tone(img);
  if (!t) return;
  tile.classList.toggle("checker-dark", t === "dark");
  tile.dataset.artTone = t;
};

export const AutoContrast = () => {
  useEffect(() => {
    document.documentElement.dataset.previewBg = readBg();
    const onLoad = (e) => { if (e.target?.tagName === "IMG") apply(e.target); };
    document.addEventListener("load", onLoad, true);
    const sweep = () => document.querySelectorAll(".checker img").forEach(apply);
    sweep();
    const mo = new MutationObserver(() => { clearTimeout(mo._t); mo._t = setTimeout(sweep, 60); });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { document.removeEventListener("load", onLoad, true); mo.disconnect(); };
  }, []);
  return null;
};
